/* ============================================================
 * frontend-v3/src/screens/versions/VersionDetailModal.tsx
 *
 * T316 / V3I-FR-005 — Category A6 decomposition: VersionDetailModal
 * Restore button.
 *
 * Per PRD §7.1 A6 the Restore button wires
 *   useMutationWithToast(api.versionHistory.restore, {...})
 * with `logLabel: "version-restore"`, `mutationName: "versionHistory.restore"`,
 * `successMessage: "Versión restaurada"`.
 *
 * Args contract (REAL deployed signature, verified at
 *   convex/versionHistory.ts:296):
 *   eventId:  Id<"versionHistory">
 *   userName: string
 *
 * Mutation-policy: do NOT add `versionHistory.restore` to RETRY_SAFE.
 * Restore is destructive of operator intent (re-running might overwrite
 * a newer manual edit) — per PRD §7.1 A6 explicit note.
 *
 * Decomposition (PRD §12 deliberate rejection 7): per-surface.
 * ============================================================ */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Avatar, Btn, EmptyState, Modal, Pill, SkeletonList,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';

interface VersionDetailModalProps {
  open: boolean;
  onClose: () => void;
  entity?: string;
}

type VersionEvent = {
  _id: string;
  createdAtMs?: number;
  createdAt?: number;
  actorName?: string;
  actorInitials?: string;
  entityKind?: string;
  summary?: string;
  description?: string;
  userName?: string;
};

export function VersionDetailModal({
  open,
  onClose,
  entity = 'Canal',
}: VersionDetailModalProps) {
  const versions = useQuery(api.versionHistory.list, {}) as
    | VersionEvent[]
    | undefined;

  // V3I-FR-012: `versionHistory.restore` is NOT RETRY_SAFE — restore is
  // destructive of operator intent (re-running might overwrite a newer
  // manual edit). The wrapper's `mutationName` is set so the policy is
  // consulted, but the entry is intentionally absent — Retry will not
  // surface on the error toast.
  const { run: restoreVersion, loading } = useMutationWithToast(
    api.versionHistory.restore,
    {
      logLabel: 'version-restore',
      mutationName: 'versionHistory.restore',
      successMessage: 'Versión restaurada',
    },
  );

  async function onRestore(eventId: string) {
    try {
      await restoreVersion({
        eventId: eventId as unknown as Id<'versionHistory'>,
        // The userName is sourced from the wired identity once the
        // operator-profile hook lands; for now we pass a stable
        // operator label — never PII (V3I-INV-12 keys-not-values is
        // a complementary guarantee on the LOGGED keys; this payload
        // value flows to the backend's audit row, which already
        // expects a string).
        userName: 'operator',
      });
      onClose();
    } catch {
      // Failure toast already surfaced by the wrapper. Keep the modal
      // open so the operator can inspect alternatives.
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={'Historial — ' + entity}
      width={720}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {versions === undefined ? (
          <SkeletonList rows={5} />
        ) : versions.length === 0 ? (
          <EmptyState
            icon="clock"
            title="Sin versiones"
            body="No hay versiones registradas para mostrar."
          />
        ) : (
          versions.slice(0, 5).map((v, i, arr) => {
            const date =
              v.createdAtMs ??
              v.createdAt ??
              undefined;
            return (
              <div
                key={v._id}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'center',
                  padding: '14px 0',
                  borderBottom:
                    i < Math.min(arr.length, 5) - 1
                      ? '1px solid var(--lightest-gray)'
                      : 'none',
                }}
              >
                <span
                  className="t-mono"
                  style={{
                    fontSize: 11,
                    color: 'var(--muted-slate)',
                    textTransform: 'none',
                    width: 130,
                  }}
                >
                  {date !== undefined
                    ? new Date(date).toLocaleDateString('es-MX')
                    : '—'}
                </span>
                <Avatar
                  name={v.actorName ?? v.userName ?? '—'}
                  initials={v.actorInitials ?? '?'}
                  size={26}
                />
                <Pill tone="neutral" size="sm">
                  {v.entityKind ?? '—'}
                </Pill>
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: 'var(--body-muted)',
                  }}
                >
                  {v.summary ?? v.description ?? '—'}
                </span>
                <Btn size="sm" variant="text" decorative>
                  Ver diff
                </Btn>
                <Btn
                  size="sm"
                  variant="ghost"
                  loading={loading}
                  onClick={() => void onRestore(v._id)}
                >
                  Restaurar
                </Btn>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}

export default VersionDetailModal;
